import type { Location } from 'history';
import { reduce, pathOr } from 'ramda';
import React from 'react';
import { useMemo } from 'react';
import { getLocation } from '@peloton/redux';
import { isDefined } from '@peloton/types';
import type { SearchSharedProperties } from '@engage/library';
import { useReduxState } from '@engage/redux';
import { SearchType } from './state';
import { SearchPageContext } from './types';
import { useArtist } from './useArtist';

const useListPosition = (location: Location) =>
  useMemo(() => pathOr(undefined, ['state', 'listPosition'], location), [location]);

/**
 * Returns the appropriate search shared properties based on the UI state.
 * @returns search shared properties
 */
export const useSearchSharedProperties = (): SearchSharedProperties | undefined => {
  const {
    pageState: { status, artistId, artistListPosition, query, searchType },
  } = React.useContext(SearchPageContext);

  const location = useReduxState(getLocation);
  const listPosition = useListPosition(location);
  const artist = useArtist();
  const artistName = artist?.name;

  return useMemo(() => {
    if (status === 'viewingArtist' && isDefined(artistName)) {
      return {
        '[Search Artist ID]': artistId,
        '[Search Artist List Position]': artistListPosition,
        '[Search Artist Name]': artistName,
        '[Search List Position]': listPosition,
        '[Search Result Type]': 'Artist Search',
        '[Search Term]': query,
        '[Search Type]':
          searchType === SearchType.ManualSearch ? 'Manual Search' : 'Popular Query',
      };
    } else if (status === 'showResults') {
      return {
        '[Search List Position]': listPosition,
        '[Search Result Type]': 'Class Search',
        '[Search Term]': query,
        '[Search Type]':
          searchType === SearchType.ManualSearch ? 'Manual Search' : 'Popular Query',
      };
    } else {
      return undefined;
    }
  }, [status, artistId, artistListPosition, query, searchType, listPosition, artistName]);
};

export const stringifyValues = (input: Record<string, unknown>): Record<string, string> =>
  reduce(
    (acc, [key, value]) => ({ ...acc, [key]: String(value) }),
    {},
    Object.entries(input),
  );
